import { Button } from '@/components/ui';
import { ProjectCustomerItem } from './ProjectCustomerItem';
import { createDefaultCustomerFormItem } from '../types';
import type { ProjectCustomerFormItem, CustomerListItem } from '../types';

interface ProjectCustomersManagerProps {
  customers: ProjectCustomerFormItem[];
  availableCustomers: CustomerListItem[];
  onChange: (customers: ProjectCustomerFormItem[]) => void;
  onCustomerSearch: (term: string) => void;
  onClearCustomerSearch: () => void;
  isLoadingCustomers: boolean;
  isSearchingCustomers: boolean;
  disabled?: boolean;
  errors?: Record<string, string>;
}

export function ProjectCustomersManager({
  customers,
  availableCustomers,
  onChange,
  onCustomerSearch,
  onClearCustomerSearch,
  isLoadingCustomers,
  isSearchingCustomers,
  disabled = false,
  errors = {},
}: ProjectCustomersManagerProps) {
  // Handle field change for a specific customer
  const handleCustomerChange = (tempId: string, updates: Partial<ProjectCustomerFormItem>) => {
    const updatedCustomers = customers.map((customer) => {
      if (customer.tempId !== tempId) {
        // If setting this customer as billing, unset others
        if (updates.isBilling === true) {
          return { ...customer, isBilling: false };
        }
        return customer;
      }

      return { ...customer, ...updates };
    });

    onChange(updatedCustomers);
  };

  // Handle setting a customer as primary
  const handleSetPrimary = (tempId: string) => {
    const updatedCustomers = customers.map((customer) => ({
      ...customer,
      isPrimary: customer.tempId === tempId,
    }));
    onChange(updatedCustomers);
  };

  // Handle removing a customer
  const handleRemoveCustomer = (tempId: string) => {
    const customerToRemove = customers.find((c) => c.tempId === tempId);
    let updatedCustomers = customers.filter((c) => c.tempId !== tempId);

    // If we removed the primary customer, set the first remaining as primary
    if (customerToRemove?.isPrimary && updatedCustomers.length > 0) {
      updatedCustomers = updatedCustomers.map((c, index) => ({
        ...c,
        isPrimary: index === 0,
      }));
    }

    // If we removed the billing customer, set the primary as billing
    if (customerToRemove?.isBilling && updatedCustomers.length > 0) {
      const primaryIndex = updatedCustomers.findIndex((c) => c.isPrimary);
      if (primaryIndex >= 0) {
        updatedCustomers = updatedCustomers.map((c, index) => ({
          ...c,
          isBilling: index === primaryIndex,
        }));
      }
    }

    onChange(updatedCustomers);
  };

  // Handle adding a new customer
  const handleAddCustomer = () => {
    const newCustomer = createDefaultCustomerFormItem(false, false);
    onChange([...customers, newCustomer]);
  };

  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-6">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-secondary-900">
            Project Customers
          </h2>
          <p className="text-sm text-secondary-500">
            Add one or more customers to this project
          </p>
        </div>
      </div>

      {/* Validation Error */}
      {errors.customers && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{errors.customers}</p>
        </div>
      )}

      {/* Customer List */}
      <div className="space-y-4">
        {customers.map((customer, index) => (
          <ProjectCustomerItem
            key={customer.tempId}
            customer={customer}
            index={index}
            customers={availableCustomers}
            onChange={handleCustomerChange}
            onRemove={handleRemoveCustomer}
            onSetPrimary={handleSetPrimary}
            onCustomerSearch={onCustomerSearch}
            onClearCustomerSearch={onClearCustomerSearch}
            isLoadingCustomers={isLoadingCustomers}
            isSearchingCustomers={isSearchingCustomers}
            canRemove={customers.length > 1}
            disabled={disabled}
            errors={errors}
          />
        ))}
      </div>

      {/* Add Customer Button */}
      <div className="mt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleAddCustomer}
          disabled={disabled}
        >
          <svg
            className="w-4 h-4 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Another Customer
        </Button>
      </div>

      {/* Help Text */}
      <div className="mt-4 p-3 bg-secondary-50 rounded-lg">
        <p className="text-sm text-secondary-600">
          <span className="font-medium">Primary Contact:</span> Main point of contact for the project.{' '}
          <span className="font-medium">Billing Contact:</span> Receives invoices and payment requests.
        </p>
      </div>
    </div>
  );
}
